import { ActivatedRoute, Router } from '@angular/router';
import { BlogArticle } from '@shared/models/blog-article.model';
import { BlogService, CookieConsentService, TrackingService } from '@core/services';
import { Component, HostListener, Inject, OnDestroy, OnInit, DOCUMENT } from '@angular/core';
import { environment } from '@env/environment';
import { GhGlobal } from '@shared/helpers/global-functions';
import { IMetaTag, MetaTagAttr } from '@core/seo/social-share/meta-tag.interface';
import { Location } from '@angular/common';
import { rewriteYoutubeEmbeds } from '@shared/helpers/youtube-embeds';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';

@Component({
    selector: 'gh-article',
    templateUrl: 'article.component.html',
    standalone: false
})

export class ArticleComponent implements OnInit, OnDestroy {
  articleDetails: any;
  articleHtml = '';
  permaLink = '';

  private parametersObservable: any;
  private consentSub: Subscription;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private blogService: BlogService,
    private cookieConsent: CookieConsentService,
    private trackingService: TrackingService,
    protected route: ActivatedRoute,
    protected location: Location,
    protected router: Router,
  ) { }

  @HostListener('click', ['$event'])
  onClick(event) {
    if (event.target.offsetParent.className == 'btn btn-red') {
      this.ctaClick(event.target)
      console.log(event);
    }
  }

  ngOnInit() {
    GhGlobal.injectStyle('', this.document);
    this.document.body.classList.add('article');
    this.parametersObservable = this.route.params
      .subscribe((params) => {
        // "articleDetails" is obtained from 'ArticleResolver'
        this.articleDetails = this.route.snapshot.data['article'];
        if (!this.articleDetails || !this.articleDetails.slug) {
          this.notFound();
          return;
        }
        GhGlobal.injectStyle(this.articleDetails.category.color, this.document);
        this.setSeoData(this.articleDetails);
        this.setPopularity(this.articleDetails.slug);
        this.applyYoutubeConsent();
      });
    this.consentSub = this.cookieConsent.consentChanges$
      .subscribe(() => this.applyYoutubeConsent());
  }

  ngOnDestroy() {
    if (this.parametersObservable != null) {
      this.parametersObservable.unsubscribe();
    }
    if (this.consentSub) {
      this.consentSub.unsubscribe();
    }
  }

  navClick(method: string, location: string) {
    this.trackingService.setDataLayerAttributes({
      event: 'interactionEvent',
      event_name: 'share',
      events: {
        feature: 'blog',
        environment: environment.env,
        post_name: `${this.articleDetails.title} | GoHenry`,
        method,
        location
      }
    });
  }

  hashClick(term: string, location: string) {
    this.trackingService.setDataLayerAttributes({
      event: 'interactionEvent',
      event_name: 'submit_search',
      events: {
        feature: 'blog',
        environment: environment.env,
        click_text: term,
        location
      }
    });
  }

  ctaClick(btn: Element) {
    this.trackingService.setDataLayerAttributes({
      event: 'interactionEvent',
      event_name: 'cta_click',
      events: {
        feature: 'blog',
        environment: environment.env,
        click_text: btn.innerHTML,
        location: 'article'
      }
    });
  }

  private applyYoutubeConsent() {
    const content = this.articleDetails?.content;
    this.articleHtml = content
      ? rewriteYoutubeEmbeds(content, this.cookieConsent.hasTargetingConsent())
      : '';
  }

  private notFound() {
    this.router.navigate(['/404']);
  }

  private setSeoData(article: BlogArticle) {
    this.permaLink = environment.location + '/' + article.category.slug + '/' + article.slug;
    const goTags = article.tags ? article.tags.split(' ').join() : 'gohenry';
    const tags: IMetaTag = { attr: MetaTagAttr.property, attrValue: 'article:tag', value: goTags };
    const title = `${article.title} | GoHenry`;

    this.trackingService.setDataLayerAttributes({
      event: 'pageView',
      event_name: 'page_view',
      page: {
        location: this.location.path(),
        environment: environment.env,
        locale: environment.locale,
        feature: 'blog',
        title,
      },
      events: {
        post_name: title,
        post_category: article.category.name,
        hashtags: tags?.value?.split(','),
      }
    });
  }

  private setPopularity(slug: string) {
    this.blogService.addPopularity(slug)
      .pipe(take(1))
      .subscribe();
  }
}
